import { useAuth0 } from '@auth0/auth0-react';
import classNames from 'classnames';
import React, { useCallback, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { maxDevices } from '../../../../constants/app';
import { AppConfig } from '../../../../constants/config';
import { getDeviceClient } from '../../../../api/devices/client';
import styles from '../../WifiConnect.module.scss';
import { EditForm, EditFormInterface } from '../Forms/EditForm';
import { FormState } from '../Forms/Form';
import { Modal, ModalInterface } from '../Modal';
import DeviceTableRow from './DeviceTableRow';
import { MemberLocation } from '../../../../api/locations/client';
import Loader from 'react-loader-spinner';

export type Device = {
  mac: string;
  type: string;
  name: string;
  location_uuid: string;
  notes: string;
  COMPANY_ID: string;
  COMPANY_NAME: string;
};

export type DevicesTableProps = {
  devices: Device[];
  locations: MemberLocation[];
  onUpdate: () => void;
  onMessage: (msg: JSX.Element) => void;
  hasAccessToCorpDevices: boolean;
};

export const DevicesTable: React.FC<DevicesTableProps> = ({
  devices,
  locations,
  onUpdate,
  onMessage,
  hasAccessToCorpDevices,
}) => {
  const { getAccessTokenSilently } = useAuth0();

  const alertRef = useRef<ModalInterface>(null);
  const confirmRef = useRef<ModalInterface>(null);
  const editFormRef = useRef<EditFormInterface>(null);

  const [callback, setCallback] = useState<Function>();
  const [currentMAC, setCurrentMAC] = useState<string>('');
  const [currentCompanyID, setCurrentCompanyID] = useState<string>('');
  const [currentCompanyName, setCurrentCompanyName] = useState<string>('');
  const [currentName, setCurrentName] = useState<string>('');
  const [currentDevice, setCurrentDevice] = useState<FormState>();
  const [isConverting, setIsConverting] = useState<boolean>(false);

  const onDelete = useCallback(async () => {
    const token = await getAccessTokenSilently();
    const resp = await getDeviceClient(AppConfig.appEnv, token).deleteDevice(
      currentMAC,
      false,
    );

    if (!resp) {
      onUpdate();
      onMessage(
        <div
          className={classNames(
            'ray-grid__cell--span-full',
            styles.textFieldWrapper,
          )}
        >
          <div
            className={classNames(
              styles.notification,
              styles.successfulNotification,
            )}
          >
            <FormattedMessage
              id="tabs.device.messages.afterDelete"
              defaultMessage="Device ''{device}'' was deleted!"
              values={{ device: currentName }}
            />
          </div>
        </div>,
      );
    } else {
      onMessage(
        <div
          className={classNames(
            'ray-grid__cell--span-full',
            styles.textFieldWrapper,
          )}
        >
          <div
            className={classNames(styles.notification, styles.errorMsgInTable)}
          >
            <FormattedMessage
              id={`tabs.device.messages.errors.${resp.status}`}
              values={{ maxDevices: maxDevices }}
              defaultMessage="Internal error. Check your Internet connection and try again"
            />
          </div>
        </div>,
      );
    }
  }, [currentMAC, currentName, getAccessTokenSilently, onMessage, onUpdate]);

  const onConfirm = useCallback(async () => {
    setIsConverting(true);
    const token = await getAccessTokenSilently();
    const resp = await getDeviceClient(AppConfig.appEnv, token).updateDevice(
      currentMAC,
      {
        mac: currentMAC,
        name: currentName,
        type: 'WWIOT',
        wifi_corp_devices: false,
        notes: currentDevice?.notes,
        COMPANY_ID: currentCompanyID,
        COMPANY_NAME: currentCompanyName,
      },
    );

    if ('name' in resp) {
      onUpdate();
      onMessage(
        <div
          className={classNames(
            'ray-grid__cell--span-full',
            styles.textFieldWrapper,
          )}
        >
          <div
            className={classNames(
              styles.notification,
              styles.successfulNotification,
            )}
          >
            <FormattedMessage
              id="tabs.device.messages.afterConfirm"
              defaultMessage="Device ''{device}'' MAC address is now managed by corporate."
              values={{ device: currentName }}
            />
          </div>
        </div>,
      );
    } else {
      onMessage(
        <div
          className={classNames(
            'ray-grid__cell--span-full',
            styles.textFieldWrapper,
          )}
        >
          <div
            className={classNames(styles.notification, styles.errorMsgInTable)}
          >
            <FormattedMessage
              id={`tabs.device.messages.errors.${resp.status}`}
              values={{ maxDevices: maxDevices }}
              defaultMessage="Internal error. Check your Internet connection and try again"
            />
          </div>
        </div>,
      );
    }

    setIsConverting(false);
  }, [currentMAC, currentName, getAccessTokenSilently, onMessage, onUpdate]);

  return (
    <div className="ray-grid__cell--span-full">
      <Modal
        ref={alertRef}
        title={
          <FormattedMessage
            id="modal.headings.confirmation"
            defaultMessage="Are you sure you want to delete this device?"
          />
        }
        onOK={() => {
          onDelete();
          setCurrentMAC('');
          if (callback) {
            callback();
          }
        }}
        onCancel={() => {
          setCurrentMAC('');
          if (callback) {
            callback();
          }
        }}
      />

      <Modal
        ref={confirmRef}
        title={
          <FormattedMessage
            id="modal.headings.corporateConfirmation"
            defaultMessage="Are you sure to move this device MAC address to be managed by corporate?"
          />
        }
        onOK={() => {
          onConfirm();
          setCurrentMAC('');
          if (callback) {
            callback();
          }
        }}
        onCancel={() => {
          setCurrentMAC('');
          if (callback) {
            callback();
          }
        }}
      />

      <EditForm
        ref={editFormRef}
        locations={locations}
        currentMAC={currentMAC}
        deviceData={currentDevice}
        title={
          <FormattedMessage
            id="modal.headings.editFormTitle"
            defaultMessage="Edit device"
          />
        }
        onOK={() => {
          setCurrentMAC('');
          setCurrentDevice(undefined);

          if (callback) {
            callback();
          }
        }}
        onCancel={() => {
          setCurrentMAC('');
          setCurrentDevice(undefined);

          if (callback) {
            callback();
          }
        }}
        onFinished={onUpdate}
        onMessage={onMessage}
        hasCorpDevicesAccess={hasAccessToCorpDevices}
      />

      {isConverting ? (
        <div className={styles.formLoader}>
          <Loader type="Oval" color="#00F" height={40} width={40} />
        </div>
      ) : (
        <></>
      )}

      <table className="ray-table">
        <thead>
          <tr>
            <th>
              <FormattedMessage
                id="tabs.device.table.header.columns.0"
                defaultMessage="Name"
              />
            </th>
            <th>
              <FormattedMessage
                id="tabs.device.table.header.columns.1"
                defaultMessage="MAC address"
              />
            </th>
            <th>
              <FormattedMessage
                id="tabs.device.table.header.columns.2"
                defaultMessage="Type"
              />
            </th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {devices.map((device: Device) => (
            <DeviceTableRow
              key={device.mac}
              device={device}
              onEdit={(
                mac: string,
                device: FormState,
                onCancel?: () => void,
              ) => {
                setCurrentMAC(mac);
                setCurrentDevice(device);
                setCallback(onCancel);
                setCurrentCompanyID(device.COMPANY_ID);
                setCurrentCompanyName(device.COMPANY_NAME);
                if (editFormRef.current) {
                  editFormRef.current.show();
                }
              }}
              onDelete={(mac: string, name: string, onCancel?: () => void) => {
                setCurrentMAC(mac);
                setCurrentName(name);
                setCallback(onCancel);

                if (alertRef.current) {
                  alertRef.current.show();
                }
              }}
              hasAccessToCorpDevices={hasAccessToCorpDevices}
              onConfirm={(
                mac: string,
                name: string,
                device: FormState,
                onCancel?: () => void,
              ) => {
                setCurrentMAC(mac);
                setCurrentName(name);
                setCurrentDevice(device);
                setCallback(onCancel);

                if (confirmRef.current) {
                  confirmRef.current.show();
                }
              }}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
};
